import { createSlice, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash/fp';

import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { ThemeSet, ThemeSetStyleMap } from 'dashboard/models/theme-set';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import type { StepPreview } from 'lib/interfaces/StepsConfig';

export interface ThemeSetState {
  themeSet: ThemeSet | undefined;
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  locale: InquiryIso6391LanguageCode;
  stepsPreview: StepPreview[];
  stylesMap: ThemeSetStyleMap;
}

interface MergeStepsPreviewOptions {
  startPaginationIndex: number;
  partialStepsPreview: StepPreview[];
}

const { reducer, actions } = createSlice<ThemeSetState, SliceCaseReducers<ThemeSetState>, string>({
  name: 'theme-set',
  initialState: {
    themeSet: undefined,
    inquiryTemplateVersion: undefined,
    locale: 'en-US',
    stepsPreview: [],
    stylesMap: { 'steps-styles': {}, 'components-styles': {} },
  },
  reducers: {
    setThemeSet: (state, action: PayloadAction<ThemeSet>) => {
      state.themeSet = action.payload;
    },
    setInquiryTemplateVersion: (state, action: PayloadAction<InquiryTemplateVersion>) => {
      state.inquiryTemplateVersion = action.payload;
    },
    setLocale: (state, action: PayloadAction<InquiryIso6391LanguageCode>) => {
      state.locale = action.payload;
    },
    setStepsPreview: (state, action: PayloadAction<StepPreview[]>) => {
      state.stepsPreview = action.payload;
    },
    mergeStepsPreview: (state, action: PayloadAction<MergeStepsPreviewOptions>) => {
      const copiedStepsPreview = cloneDeep(state.stepsPreview);

      copiedStepsPreview.splice(
        action.payload.startPaginationIndex,
        action.payload.partialStepsPreview.length,
        ...action.payload.partialStepsPreview,
      );

      state.stepsPreview = copiedStepsPreview;
    },
    setStylesMap: (state, action: PayloadAction<ThemeSetStyleMap>) => {
      state.stylesMap = action.payload;
    },
  },
});

export const {
  setThemeSet,
  setInquiryTemplateVersion,
  setLocale,
  setStepsPreview,
  mergeStepsPreview,
  setStylesMap,
} = actions;
export const themeSetReducer = reducer;
